import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { TrendingUp, DollarSign, Clock, BarChart3, Sparkles, Target, Upload, ArrowDown, MessageSquare, ChartLine, Heart, Calculator, Users, Utensils, Phone, PhoneIncoming, CreditCard, Smartphone } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { fadeInVariants, containerVariants } from '../ui/animations';
import { Link } from "react-router-dom";

const RevenueImpact = () => {
  const [activeProduct, setActiveProduct] = useState<'virtual-host' | 'virtual-assistant'>('virtual-host');

  // Virtual Host revenue pillars
  const virtualHostPillars = [
    {
      icon: PhoneIncoming,
      title: "AI Phone Agent",
      subtitle: "Your 24/7 Order Taker",
      metrics: [
        { label: "Answer Time", value: "<3 rings" },
        { label: "Order Accuracy", value: "99.2%" },
        { label: "Availability", value: "24/7/365" }
      ],
      features: [
        "Answers every call instantly",
        "Natural conversation with customers",
        "Handles complex orders perfectly",
        "Never puts customers on hold"
      ],
      color: "orange"
    },
    {
      icon: TrendingUp,
      title: "Revenue Recovery",
      subtitle: "Your Missed Call Rescuer",
      metrics: [
        { label: "Calls Recovered", value: "100%" },
        { label: "Avg Order Value", value: "$32" },
        { label: "Monthly Lift", value: "+$2,400" }
      ],
      features: [
        "Capture every missed call",
        "Rush hour overflow handling",
        "After-hours order taking",
        "No more voicemail dead-ends"
      ],
      color: "blue"
    },
    {
      icon: CreditCard,
      title: "Payment Processing",
      subtitle: "Your Revenue Collector",
      metrics: [
        { label: "Payment Success", value: "99.8%" },
        { label: "Deposits", value: "Next day" },
        { label: "Convenience Fee", value: "99¢-$1.99" }
      ],
      features: [
        "Secure Stripe integration",
        "Card payments over phone",
        "Automatic order confirmation",
        "Real-time transaction tracking"
      ],
      color: "purple"
    }
  ];

  // Virtual Assistant revenue pillars (existing)
  const virtualAssistantPillars = [
    {
      icon: MessageSquare,
      title: "Smart Menu Assistant",
      subtitle: "Your 24/7 Sales Pro",
      metrics: [
        { label: "Response Time", value: "<2 seconds" },
        { label: "Upsell Success", value: "2.1x more likely" },
        { label: "Weekly Revenue", value: "+$198" }
      ],
      features: [
        "Instant answers to any menu question",
        "Smart drink pairing suggestions",
        "Dietary restriction handling",
        "Upsells like your best server"
      ],
      color: "emerald"
    },
    {
      icon: ChartLine,
      title: "Revenue Intelligence",
      subtitle: "Your Data Detective",
      metrics: [
        { label: "Hidden Revenue", value: "$4,200/year" },
        { label: "Sweet Spot", value: "44.9% found" },
        { label: "Insights", value: "Weekly" }
      ],
      features: [
        "Discover hidden revenue opportunities",
        "Price optimization insights",
        "Menu performance analytics",
        "Customer preference tracking"
      ],
      color: "blue"
    },
    {
      icon: Heart,
      title: "Customer Connection",
      subtitle: "Your Relationship Builder",
      metrics: [
        { label: "Preferences", value: "100% remembered" },
        { label: "Promotions", value: "Automated" },
        { label: "Repeat Visits", value: "3x increase" }
      ],
      features: [
        "Remember every customer preference",
        "Send personalized promotions",
        "Build lasting relationships",
        "Turn strangers into regulars"
      ],
      color: "purple"
    }
  ];

  const revenuePillars = activeProduct === 'virtual-host' ? virtualHostPillars : virtualAssistantPillars;

  return (
    <section id="revenue-impact" className="py-12 bg-dineai-blue relative overflow-hidden">
      {/* Background elements */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-20 right-10 w-64 h-64 rounded-full bg-dineai-yellow/20 blur-3xl"></div>
        <div className="absolute bottom-20 left-10 w-80 h-80 rounded-full bg-[#43acff]/20 blur-3xl"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 rounded-full bg-white/10 blur-3xl"></div>
      </div>

      <div className="container mx-auto px-4 relative z-10">
        {/* Header */}
        <motion.div 
          className="text-center mb-12"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          <motion.div 
            className="inline-block bg-dineai-yellow/20 px-4 py-2 rounded-full mb-4"
            variants={fadeInVariants}
          >
            <span className="text-dineai-yellow font-medium text-sm flex items-center gap-2">
              <Sparkles className="w-4 h-4" />
              The Complete System
            </span>
          </motion.div>
          
          <motion.h2 
            className="text-2xl md:text-3xl font-bold mb-3 text-white"
            variants={fadeInVariants}
          >
            Your Restaurant's{" "}
            <span className="bg-gradient-to-r from-dineai-yellow via-yellow-300 to-dineai-yellow bg-clip-text text-transparent">
              AI Revenue Engine
            </span>
          </motion.h2>
          
          <motion.p
            className="text-white/80 text-base mx-auto max-w-3xl mb-6"
            variants={fadeInVariants}
          >
            {activeProduct === 'virtual-host'
              ? "AI-powered phone ordering that captures every call and processes payments automatically"
              : "Beyond answering questions - discover, delight, and drive revenue 24/7"
            }
          </motion.p>

          {/* Product Toggle Tabs */}
          <motion.div className="flex justify-center" variants={fadeInVariants}>
            <div className="inline-flex bg-white/10 rounded-full p-1 backdrop-blur-sm border border-white/20">
              <button
                onClick={() => setActiveProduct('virtual-host')}
                className={`px-6 py-2.5 rounded-full font-semibold text-sm transition-all duration-300 flex items-center gap-2 ${
                  activeProduct === 'virtual-host'
                    ? 'bg-dineai-yellow text-black shadow-lg'
                    : 'text-white/70 hover:text-white hover:bg-white/10'
                }`}
              >
                <Phone className="w-4 h-4" />
                AI Virtual Host
              </button>
              <button
                onClick={() => setActiveProduct('virtual-assistant')}
                className={`px-6 py-2.5 rounded-full font-semibold text-sm transition-all duration-300 flex items-center gap-2 ${
                  activeProduct === 'virtual-assistant'
                    ? 'bg-dineai-yellow text-black shadow-lg'
                    : 'text-white/70 hover:text-white hover:bg-white/10'
                }`}
              >
                <Smartphone className="w-4 h-4" />
                AI Virtual Assistant
              </button>
            </div>
          </motion.div>
        </motion.div>

        {/* Revenue Pillars */}
        <AnimatePresence mode="wait">
          <motion.div
            key={activeProduct}
            className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-12 mt-8"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
          >
          {revenuePillars.map((pillar, index) => {
            const colorClasses = {
              orange: {
                icon: 'text-orange-400',
                bg: 'bg-orange-400/10',
                border: 'border-orange-400/20',
                hoverBorder: 'hover:border-orange-400/40',
                shadow: 'hover:shadow-orange-400/20',
                glow: 'group-hover:drop-shadow-[0_0_8px_rgba(251,146,60,0.5)]'
              },
              emerald: {
                icon: 'text-emerald-400',
                bg: 'bg-emerald-400/10',
                border: 'border-emerald-400/20',
                hoverBorder: 'hover:border-emerald-400/40',
                shadow: 'hover:shadow-emerald-400/20',
                glow: 'group-hover:drop-shadow-[0_0_8px_rgba(52,211,153,0.5)]'
              },
              blue: {
                icon: 'text-blue-400',
                bg: 'bg-blue-400/10',
                border: 'border-blue-400/20',
                hoverBorder: 'hover:border-blue-400/40',
                shadow: 'hover:shadow-blue-400/20',
                glow: 'group-hover:drop-shadow-[0_0_8px_rgba(96,165,250,0.5)]'
              },
              purple: {
                icon: 'text-purple-400',
                bg: 'bg-purple-400/10',
                border: 'border-purple-400/20',
                hoverBorder: 'hover:border-purple-400/40',
                shadow: 'hover:shadow-purple-400/20',
                glow: 'group-hover:drop-shadow-[0_0_8px_rgba(167,139,250,0.5)]'
              }
            };
            
            const colors = colorClasses[pillar.color as keyof typeof colorClasses];
            
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                className="group"
              >
                <div className="backdrop-blur-sm rounded-xl p-6 border border-yellow-400/30 shadow-lg hover:shadow-xl hover:border-yellow-400/50 hover:scale-105 transition-all duration-300 h-full cursor-pointer" style={{ backgroundColor: '#3256dc' }}>
                  {/* Header */}
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <pillar.icon className={`w-10 h-10 ${colors.icon} ${colors.glow} transition-all duration-300 mb-3`} strokeWidth={1.5} />
                      <h3 className="text-xl font-bold text-white mb-1">{pillar.title}</h3>
                      <p className={`${colors.icon} text-sm font-medium`}>{pillar.subtitle}</p>
                    </div>
                  </div>
                  
                  {/* Metrics */}
                  <div className="space-y-3 mb-6">
                    {pillar.metrics.map((metric, idx) => (
                      <div key={idx} className={`${colors.bg} rounded-lg p-3`}>
                        <div className="flex justify-between items-center">
                          <span className="text-white/70 text-sm">{metric.label}</span>
                          <span className={`${colors.icon} font-bold`}>{metric.value}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  {/* Features */}
                  <ul className="space-y-2">
                    {pillar.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-2">
                        <span className={`${colors.icon} text-sm mt-0.5`}>•</span>
                        <span className="text-white/70 text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  
                  {/* Hover Action */}
                  <div className="mt-6 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <div className={`text-center py-2 ${colors.icon} font-bold text-sm`}>
                      Learn More →
                    </div>
                  </div>
                </div>
              </motion.div>
            );
          })}
          </motion.div>
        </AnimatePresence>


        {/* Bottom CTA */}
        <motion.div
          className="text-center"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          <p className="text-white/70 mb-6 text-lg">
            {activeProduct === 'virtual-host'
              ? "Ready to never miss another call?"
              : "Ready to transform your restaurant's revenue?"
            }
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
            <Link to="/restaurant-menu-search">
              <Button className="bg-dineai-yellow text-black hover:bg-dineai-yellow/90 py-4 sm:py-6 px-6 sm:px-8 text-base sm:text-lg font-bold rounded-full shadow-lg transform hover:scale-105 transition-all flex items-center gap-2">
                {activeProduct === 'virtual-host' ? (
                  <>
                    <Phone className="w-5 h-5" />
                    GET YOUR AI PHONE NUMBER
                  </>
                ) : (
                  <>
                    <Utensils className="w-5 h-5" />
                    SIGN UP NOW
                  </>
                )}
              </Button>
            </Link>
            <Link to={activeProduct === 'virtual-host' ? '/products/virtual-host#pricing' : '/case-studies'}>
              <Button className="bg-transparent border-2 border-[#43acff] text-[#43acff] hover:bg-[#43acff] hover:text-white hover:border-[#43acff] py-4 sm:py-6 px-6 sm:px-8 text-base sm:text-lg font-medium rounded-full flex items-center gap-2 transition-all">
                {activeProduct === 'virtual-host' ? 'SEE PRICING PLANS' : 'VIEW CASE STUDY'}
                <ArrowDown className="w-4 sm:w-5 h-4 sm:h-5 rotate-[-90deg]" />
              </Button>
            </Link>
          </div>
          
          <div className="flex items-center justify-center gap-3 text-white/60 text-sm">
            <Users className="w-4 h-4 text-dineai-yellow" />
            <span>Join fastest growing community of restaurants already growing with Dine AI</span>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default RevenueImpact;
